import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { AuthCardLayout } from "@/shared/components/AuthCardLayout";
import { authService } from "@/core/services/authService";
import { sanitizarCpf, cpfEhValido } from "@/core/utils/cpf";

const schema = z.object({
  cpf: z
    .string()
    .min(1, "Informe seu CPF.")
    .refine((valor) => cpfEhValido(valor), "CPF inválido. Use apenas os 11 números."),
});

type FormData = z.infer<typeof schema>;

export function EsqueciSenha() {
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(dados: FormData) {
    setErro(null);
    try {
      await authService.solicitarRedefinicaoSenha(sanitizarCpf(dados.cpf));
      setEnviado(true);
    } catch (e) {
      // Frente 1 (Sprint 5, 02/08/2026): antes engolia qualquer erro (ex.:
      // rate limit de redefinição) na mesma mensagem genérica. Mesmo padrão
      // já usado em RedefinirSenha.tsx — authService.ts continua responsável
      // por nunca deixar vazar se um CPF existe ou não.
      setErro(
        e instanceof Error ? e.message : "Não foi possível processar sua solicitação. Tente novamente.",
      );
    }
  }

  return (
    <AuthCardLayout
      titulo="Esqueci minha senha"
      rodape={
        <Link to="/login" className="font-bold text-primary hover:underline">
          Voltar para o login
        </Link>
      }
    >
      {enviado ? (
        <div className="rounded-md bg-primary/5 px-4 py-3 text-sm leading-relaxed text-text">
          <p>
            Se o CPF informado estiver cadastrado, você vai receber um e-mail com instruções para
            redefinir sua senha em instantes.
          </p>
          <p className="mt-3 text-xs text-muted">
            Não recebeu? Verifique também a caixa de spam. Se o problema persistir, contate um
            administrador.
          </p>
        </div>
      ) : (
        <>
          <p className="mb-5 text-sm text-muted">
            Informe seu CPF. Enviaremos um link de redefinição para o e-mail cadastrado no seu
            perfil.
          </p>
          <div className="mb-4 flex gap-2 rounded-md bg-primary/5 px-3 py-2.5 text-xs text-muted">
            <span>
              O e-mail usado é o de login da sua conta, não necessariamente o e-mail de contato
              mostrado no seu perfil.
            </span>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <Input
              label="CPF"
              placeholder="000.000.000-00"
              inputMode="numeric"
              autoComplete="username"
              erro={errors.cpf?.message}
              {...register("cpf")}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                e.target.value = value;
              }}
            />

            {erro && (
              <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
                {erro}
              </p>
            )}

            <Button type="submit" carregando={isSubmitting} className="mt-1">
              Enviar link de redefinição
            </Button>
          </form>
        </>
      )}
    </AuthCardLayout>
  );
}
